import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ScrollText, Download, Check, X, MinusCircle, Paperclip, ChevronDown, ChevronRight, FileText, Gavel, BadgeCheck, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { RulePanel } from "@/components/RulePanel";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import { RelatedDocuments, type RelatedDocument } from "@/components/RelatedDocuments";
import { FileUpload } from "@/components/FileUpload";
import { WorksPermitReviewModal, type WorksPermit } from "@/components/WorksPermitReviewModal";
import { RESOLUTION_DOC_KINDS } from "@costadorada/shared";

type VoteChoice = "YES" | "NO" | "ABSTAIN";

interface Resolution {
  id: string;
  title: string;
  description: string;
  proposedBy: string;
  status: "DRAFT" | "VOTING" | "APPROVED" | "REJECTED";
  fileUrl?: string | null;
  votedAt?: string | null;
  createdAt: string;
  votes: Array<{ id: string; vote: VoteChoice; userId: string }>;
  meeting?: { id: string; title: string } | null;
  attachments?: RelatedDocument[];
  /** The works permit (Solicitud de Obra) this resolution decides, with what was submitted. */
  worksPermit?: PermitSummary | null;
  /** The official count: only active board seats, against how many seats there are. */
  voting?: { eligible: number; cast: number; yes: number; no: number; abstain: number };
}

interface PermitSummary {
  id: string;
  status: WorksPermit["status"];
  workType: string;
  description: string;
  startDate: string;
  estimatedEndDate: string;
  applicantName?: string | null;
  contractorName?: string | null;
  contractorCompany?: string | null;
  contractorPhone?: string | null;
  reviewNotes?: string | null;
  reviewedAt?: string | null;
  unit: { unitNumber: string; ownerName: string };
  attachments: Array<{ id: string; kind: string; title: string; fileUrl: string }>;
}

const SIGNED = "RESOLUCION_FIRMADA";

const statusVariant: Record<string, "default" | "info" | "warning" | "success" | "danger"> = {
  DRAFT: "default",
  VOTING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
};

export function ResolutionsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Resolution | null>(null);
  const [deciding, setDeciding] = useState<WorksPermit | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);

  const { data: resolutions = [], isLoading } = useQuery({
    queryKey: ["resolutions"],
    queryFn: async () => (await api.get<Resolution[]>("/resolutions")).data,
  });

  // Links to a single resolution (from a permit, or the board's email) use #res-<id>.
  const { hash } = useLocation();
  const focusId = hash.startsWith("#res-") ? hash.slice(5) : null;
  useEffect(() => {
    if (!focusId || resolutions.length === 0) return;
    requestAnimationFrame(() => document.getElementById(`res-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [focusId, resolutions.length]);

  // The decision itself is the permit review (same notes, AI analysis and audit as in
  // Solicitudes); it marks the permit and moves this resolution in one step.
  async function openDecision(permitId: string) {
    setDecideError(null);
    try {
      setDeciding((await api.get<WorksPermit>(`/works-permits/${permitId}`)).data);
    } catch (e: any) {
      setDecideError(e?.response?.data?.message ?? t("common.error"));
    }
  }

  const voteMutation = useMutation({
    mutationFn: async ({ id, vote }: { id: string; vote: VoteChoice }) =>
      (await api.post(`/resolutions/${id}/vote`, { vote })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resolutions"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/resolutions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resolutions"] }),
  });

  // The administration closes a vote that will not complete (a member who never votes):
  // it is decided on the votes cast, by the same majority rule.
  const closeMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/resolutions/${id}/close`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resolutions"] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? t("common.error")),
  });
  function confirmClose(r: Resolution) {
    const v = r.voting;
    const outcome = v && v.yes > v.no && v.yes > 0 ? t("resolutions.approved") : t("resolutions.rejected");
    const msg = v
      ? t("resolutions.closeConfirm", { cast: v.cast, eligible: v.eligible, yes: v.yes, no: v.no, abstain: v.abstain, outcome })
      : t("resolutions.closeConfirmShort");
    if (window.confirm(msg)) closeMutation.mutate(r.id);
  }

  const canCreate = user && (user.role === "ADMIN" || user.role === "BOARD_MEMBER");
  // Only a seat on the Junta votes; the result counts nobody else.
  const canVote = !!user?.boardPosition;
  const canClose = user?.role === "ADMIN";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ScrollText size={24} /> {t("resolutions.title")}
        </h1>
        {canCreate && (
          <button onClick={() => setOpen(true)} className="btn-primary">
            <Plus size={16} /> {t("resolutions.create")}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : resolutions.length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : (
        <div className="space-y-4">
          {resolutions.map((r) => {
            const yes = r.voting?.yes ?? r.votes.filter((v) => v.vote === "YES").length;
            const no = r.voting?.no ?? r.votes.filter((v) => v.vote === "NO").length;
            const abst = r.voting?.abstain ?? r.votes.filter((v) => v.vote === "ABSTAIN").length;
            const myVote = r.votes.find((v) => v.userId === user?.id)?.vote;
            const isOpen = r.status === "VOTING";
            const permit = r.worksPermit;
            const statusLabel =
              permit && r.status === "DRAFT" ? t("resolutions.pendingDecision") : t(`resolutions.${r.status.toLowerCase()}`);

            return (
              <div
                key={r.id}
                id={`res-${r.id}`}
                className={`card scroll-mt-4 ${focusId === r.id ? "ring-2 ring-primary" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={permit && r.status === "DRAFT" ? "info" : statusVariant[r.status]}>{statusLabel}</Badge>
                      {permit && (
                        <Badge variant="default">
                          <FileText size={11} className="mr-1" /> {t("resolutions.permitBadge")}
                        </Badge>
                      )}
                      {r.meeting && (
                        <Link to={`/meetings/${r.meeting.id}`} className="text-xs text-primary hover:underline">
                          · {r.meeting.title}
                        </Link>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold">{r.title}</h3>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{r.description}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {t("resolutions.proposedBy")}: {r.proposedBy} · {formatDate(r.createdAt, i18n.language)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-3 shrink-0">
                    {(r.status === "APPROVED" || r.status === "REJECTED") && (
                      <SignedResolution
                        resolution={r}
                        canUpload={!!canCreate}
                        onChanged={() => {
                          qc.invalidateQueries({ queryKey: ["resolutions"] });
                          qc.invalidateQueries({ queryKey: ["works-permits"] });
                        }}
                      />
                    )}
                    {r.fileUrl && (
                      <a href={r.fileUrl} download={`resolucion-${r.id}.pdf`} className="btn-secondary">
                        <Download size={14} /> PDF
                      </a>
                    )}
                    <AdminRecordActions
                      entityLabel={r.title}
                      onEdit={() => setEditing(r)}
                      onDelete={() => deleteMutation.mutate(r.id)}
                    />
                  </div>
                </div>

                {permit && (
                  <PermitSection
                    permit={permit}
                    canDecide={!!canCreate && r.status === "DRAFT"}
                    onDecide={() => openDecision(permit.id)}
                  />
                )}

                <ResolutionDocuments
                  resolution={r}
                  canEdit={!!canCreate}
                  canDelete={user?.role === "ADMIN"}
                  onChanged={() => qc.invalidateQueries({ queryKey: ["resolutions"] })}
                />

                {/* A works permit is decided directly (Revisar y decidir), not voted. */}
                {!permit && (
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">{t("resolutions.tally")}:</span>{" "}
                    <span className="text-green-600">{yes} {t("resolutions.yes")}</span> ·{" "}
                    <span className="text-red-600">{no} {t("resolutions.no")}</span> ·{" "}
                    <span className="text-slate-500">{abst} {t("resolutions.abstain")}</span>
                    {r.voting && (
                      <span className="text-slate-500">
                        {" · "}
                        {t("resolutions.votedOf", { cast: r.voting.cast, eligible: r.voting.eligible })}
                      </span>
                    )}
                  </div>

                  {isOpen && (canVote || canClose) && (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                  {!canVote && <span className="text-xs text-slate-500">{t("resolutions.onlyBoardVotes")}</span>}
                  {canClose && (
                    <button className="btn-secondary" onClick={() => confirmClose(r)} disabled={closeMutation.isPending}>
                      <Lock size={14} /> {t("resolutions.closeVoting")}
                    </button>
                  )}
                  {canVote && (
                    <div className="flex gap-2">
                      <button
                        className={`btn ${myVote === "YES" ? "bg-green-600 text-white" : "bg-green-50 text-green-700 hover:bg-green-100"}`}
                        onClick={() => voteMutation.mutate({ id: r.id, vote: "YES" })}
                        disabled={voteMutation.isPending}
                      >
                        <Check size={14} /> {t("resolutions.yes")}
                      </button>
                      <button
                        className={`btn ${myVote === "NO" ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
                        onClick={() => voteMutation.mutate({ id: r.id, vote: "NO" })}
                        disabled={voteMutation.isPending}
                      >
                        <X size={14} /> {t("resolutions.no")}
                      </button>
                      <button
                        className={`btn ${myVote === "ABSTAIN" ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                        onClick={() => voteMutation.mutate({ id: r.id, vote: "ABSTAIN" })}
                        disabled={voteMutation.isPending}
                      >
                        <MinusCircle size={14} /> {t("resolutions.abstain")}
                      </button>
                    </div>
                  )}
                  </div>
                  )}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {decideError && (
        <div className="fixed bottom-4 right-4 z-50 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 shadow">
          {decideError}
        </div>
      )}
      <WorksPermitReviewModal
        permit={deciding}
        onClose={() => setDeciding(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ["resolutions"] })}
      />
      <CreateResolutionModal
        open={open || !!editing}
        editing={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
      />
    </div>
  );
}

/**
 * The signed resolution, front and centre: once the paper copy is signed, it is scanned
 * and uploaded here (a RESOLUCION_FIRMADA document). The owner then gets it from their
 * permit instead of the unsigned PDF.
 */
function SignedResolution({
  resolution,
  canUpload,
  onChanged,
}: {
  resolution: Resolution;
  canUpload: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  // The API returns documents newest first, so the first signed one is the current copy.
  const signed = (resolution.attachments ?? []).find((a) => a.kind === SIGNED);
  const upload = useMutation({
    mutationFn: async (fileUrl: string) =>
      (await api.post(`/resolutions/${resolution.id}/attachments`, { kind: SIGNED, title: t("resolutions.signedTitle"), fileUrl })).data,
    onSuccess: onChanged,
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      {signed && (
        <>
          <Badge variant="success">
            <BadgeCheck size={11} className="mr-1" /> {t("resolutions.signed")}
          </Badge>
          <a href={signed.fileUrl} target="_blank" rel="noreferrer" className="btn-secondary">
            <Download size={14} /> {t("resolutions.downloadSigned")}
          </a>
        </>
      )}
      {canUpload && (
        <FileUpload
          folder="costadorada1/resolutions"
          accept=".pdf,.jpg,.jpeg,.png"
          buttonLabel={signed ? t("resolutions.replaceSigned") : t("resolutions.uploadSigned")}
          onUploaded={(url) => url && upload.mutate(url)}
        />
      )}
      {upload.isError && (
        <span className="text-xs text-red-600">{(upload.error as any)?.response?.data?.message ?? t("common.error")}</span>
      )}
    </div>
  );
}

/** What the permit asked for and every document submitted with it, so the decision (and anyone reading it later) has the request at hand. */
function PermitSection({ permit, canDecide, onDecide }: { permit: PermitSummary; canDecide: boolean; onDecide: () => void }) {
  const { t, i18n } = useTranslation();
  const contractor = [permit.contractorName, permit.contractorCompany, permit.contractorPhone].filter(Boolean).join(" · ");
  return (
    <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-slate-700 inline-flex items-center gap-1.5">
          <FileText size={14} /> {t("resolutions.permitSection")}
        </div>
        <Link to={`/resident/works-permit?permit=${permit.id}`} className="text-xs text-primary hover:underline">
          {t("resolutions.viewPermit")} →
        </Link>
      </div>
      <div className="grid gap-1 sm:grid-cols-2 text-slate-700">
        <div><span className="text-slate-500">{t("resolutions.permitUnit")}:</span> {permit.unit.unitNumber} · {permit.applicantName || permit.unit.ownerName}</div>
        <div><span className="text-slate-500">{t("worksPermits.workType")}:</span> {permit.workType}</div>
        <div>
          <span className="text-slate-500">{t("resolutions.permitPeriod")}:</span>{" "}
          {formatDateOnly(permit.startDate, i18n.language)} – {formatDateOnly(permit.estimatedEndDate, i18n.language)}
        </div>
        {contractor && <div><span className="text-slate-500">{t("worksPermits.contractorName")}:</span> {contractor}</div>}
      </div>
      {permit.reviewNotes && (
        <div className="text-slate-700 whitespace-pre-line">
          <span className="text-slate-500">{t("worksPermits.reviewNotes")}:</span> {permit.reviewNotes}
        </div>
      )}
      <div>
        <div className="text-xs font-medium text-slate-600">
          {t("resolutions.submittedDocs")} ({permit.attachments.length})
        </div>
        {permit.attachments.length === 0 ? (
          <p className="text-xs text-slate-500 mt-1">{t("resolutions.noSubmittedDocs")}</p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-2">
            {permit.attachments.map((a) => (
              <a
                key={a.id}
                href={a.fileUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-primary hover:underline"
              >
                <Paperclip size={11} /> <span className="text-slate-500">{t(`worksPermits.docKinds.${a.kind}`)}:</span> {a.title}
              </a>
            ))}
          </div>
        )}
      </div>
      {canDecide && (
        <div className="pt-1">
          <button type="button" className="btn-primary text-sm" onClick={onDecide}>
            <Gavel size={14} /> {t("resolutions.decide")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Documents that support a resolution (proposal, quotes, reports, the signed resolution…), folded under each card. */
function ResolutionDocuments({
  resolution,
  canEdit,
  canDelete,
  onChanged,
}: {
  resolution: Resolution;
  canEdit: boolean;
  canDelete: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const items = resolution.attachments ?? [];
  const [open, setOpen] = useState(items.length > 0);
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <button type="button" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-primary" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Paperclip size={14} /> {t("resolutions.attachments")} ({items.length})
      </button>
      {open && (
        <div className="mt-2">
          <RelatedDocuments
            endpoint={`/resolutions/${resolution.id}/attachments`}
            items={items}
            kinds={RESOLUTION_DOC_KINDS}
            kindLabel={(k) => t(`resolutions.attachmentKind.${k}`)}
            defaultKind="PROPUESTA"
            folder="costadorada1/resolutions"
            canEdit={canEdit}
            canDelete={canDelete}
            onChanged={onChanged}
            emptyText={t("resolutions.noAttachments")}
            highlightKinds={["RESOLUCION_FIRMADA"]}
          />
        </div>
      )}
    </div>
  );
}

function CreateResolutionModal({ open, editing, onClose }: { open: boolean; editing?: Resolution | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const empty = () => ({ title: "", description: "", proposedBy: user ? `${user.firstName} ${user.lastName}` : "", meetingId: "" });
  const [form, setForm] = useState(empty());
  // Resolutions belong to the meeting where they were taken; the API has always
  // accepted meetingId, this is the picker the form was missing.
  const { data: meetings = [] } = useQuery({
    queryKey: ["meetings-options"],
    queryFn: async () => (await api.get<Array<{ id: string; title: string; scheduledAt: string }>>("/meetings")).data,
    enabled: open,
  });

  useEffect(() => {
    if (editing) {
      setForm({ title: editing.title, description: editing.description, proposedBy: editing.proposedBy, meetingId: editing.meeting?.id ?? "" });
    } else {
      setForm(empty());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, user]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { ...form, meetingId: form.meetingId || null };
      return editing
        ? (await api.put(`/resolutions/${editing.id}`, payload)).data
        : (await api.post("/resolutions", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resolutions"] });
      onClose();
      setForm(empty());
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={editing ? t("resolutions.title") : t("resolutions.create")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <RulePanel tags={["vote", "quorum"]} />
        <div>
          <label className="label">{t("meetings.title")}</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("resolutions.description")}</label>
          <textarea className="input min-h-[140px]" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("resolutions.proposedBy")}</label>
          <input className="input" required value={form.proposedBy} onChange={(e) => setForm({ ...form, proposedBy: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("meetings.title")} <span className="text-slate-400 font-normal">(opcional)</span></label>
          <select className="input" value={form.meetingId} onChange={(e) => setForm({ ...form, meetingId: e.target.value })}>
            <option value="">—</option>
            {meetings.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title} · {formatDate(m.scheduledAt, i18n.language)}
              </option>
            ))}
          </select>
        </div>
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
